import React, { useRef, useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import getDistance from "geolib/es/getDistance";
import math from "mathjs";
import { getLeafRating } from "../../../util/leafRating";
import { getDisplayableMenuItems } from "../../shared/MenuDisplay";
import LeafRating from "../../common/LeafRating";
import RestaurantActionMenu from "../restaurantResults/RestaurantActionMenu";
import useEvent from "../../../hooks/useEvent";
import HighlightText from "../../shared/HighlightText";
import "./RestaurantCard.css";

const RestaurantCard = ({
  restaurant,
  currentLocation,
  isSelected,
  onClick,
  token,
  searchQuery,
  showDistance = true,
  leafRating: leafRatingProp,
}) => {
  const menuScrollRef = useRef(null);
  const infoRef = useRef(null);
  const [overflows, setOverflows] = useState(false);
  const { event } = useEvent();

  // Calculate distance only when shown and currentLocation is usable.
  let formattedDistance = null;
  if (showDistance && currentLocation && currentLocation.lat != null && currentLocation.lng != null) {
    const distanceInMeters = getDistance(
      currentLocation,
      restaurant.geoJson.coordinates
    );
    const distance = math.unit(distanceInMeters, "m").to("mi");
    formattedDistance = math.format(distance, { notation: "fixed", precision: 1 });
  }

  // Use precomputed rating if provided (saves re-computing when server already has it).
  const rating = leafRatingProp || getLeafRating(restaurant);

  // Get cuisines
  const cuisines = restaurant.cuisine?.filter(Boolean).map(c =>
    c.replace(/\b\w/g, l => l.toUpperCase())
  ) || [];

  // Get menu items using the shared helper (prefers allMenuItems with sections)
  const menuItems = getDisplayableMenuItems(restaurant);

  // Detect if cuisine pills overflow their container
  useEffect(function detectCuisineOverflow() {
    const el = infoRef.current;
    if (el) setOverflows(el.scrollWidth > el.clientWidth);
  }, [cuisines.length]);

  // Prevent touch events on menu scroll from bubbling to parent carousel
  const handleMenuTouchStart = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className={`restaurant-card ${isSelected ? "restaurant-card-selected" : ""}`}
      onClick={onClick}
    >
      <div className="restaurant-card-header">
        <div className="restaurant-card-name">
          {restaurant.metadata?.adHocSearch && (
            <span className="restaurant-card-adhoc-badge">Discovery</span>
          )}
          {restaurant.name}
        </div>
        <div className="restaurant-card-header-right">
          {token && (
            <div className="restaurant-card-action-menu" onClick={(e) => e.stopPropagation()}>
              <RestaurantActionMenu
                restaurant={restaurant}
                token={token}
              />
            </div>
          )}
          {formattedDistance != null && (
            <div className="restaurant-card-distance">{formattedDistance}</div>
          )}
        </div>
      </div>

      <div className="restaurant-card-details">
        <div ref={infoRef} className={`restaurant-card-info${overflows ? " restaurant-card-info-overflows" : ""}`}>
          {!event && <LeafRating count={rating.count} tier={rating.tier} />}
          {cuisines.map((cuisine, i) => (
            <span key={i} className="restaurant-card-cuisine">{cuisine}</span>
          ))}
        </div>
      </div>

      {/* Horizontally scrollable menu items */}
      {menuItems.length > 0 && (
        <div
          className="restaurant-card-menu-scroll"
          ref={menuScrollRef}
          onTouchStart={handleMenuTouchStart}
        >
          {menuItems.map((item, index) => (
            <div key={item._id || index} className="restaurant-card-menu-chip">
              <HighlightText text={item.option} query={searchQuery} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

RestaurantCard.propTypes = {
  restaurant: PropTypes.object.isRequired,
  currentLocation: PropTypes.object,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  token: PropTypes.string,
  showDistance: PropTypes.bool,
  leafRating: PropTypes.shape({
    count: PropTypes.oneOf([1, 2, 3]),
    tier: PropTypes.oneOf(["gray", "green-outline", "green-solid"]),
  }),
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
});

export default connect(mapStateToProps)(RestaurantCard);
